import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { ActivityIndicator, Box, Text, ScrollView, Button } from '../../../ui';
import { PageContent, Main, LayoutBox } from '../../../components';
import { useApi, useAsync, useNavigate } from '../../../hooks';
import { QuoteList } from '../../QuotesListView/LegacyQuoteList';
import { GenerateQuoteForApplicationButton } from '../GenerateQuoteForApplicationButton';
import { ViewTabs } from '../../../components/ViewTabs';
import { ApplicationDetails } from './ApplicationDetails';
import { ApplicationDocuments } from './ApplicationDocuments';
import { LossManagement } from '../../../organisms/LossManagement';
import { BindApplicationDialog } from '../BindApplicationDialog';

export const ApplicationViewTabs = {
  APPLICATION: 'APPLICATION',
  QUOTES: 'QUOTES',
  DOCUMENTS: 'DOCUMENTS',
  LOSS_HISTORY: 'LOSS_HISTORY',
};

export const ApplicationView = React.forwardRef(function ApplicationView(props, ref) {
  const { data = undefined, status: externalStatus, applicationId, initialTab, children, scrollView = false, ...rest } = props;
  const [viewTab, setViewTab] = useState(initialTab || ApplicationViewTabs.APPLICATION);
  const navigate = useNavigate();
  const { getApplicationWithId, getPackages } = useApi();
  const getApplication = useCallback(
    async (refresh = false) => {
      if (refresh !== true && data !== undefined) {
        return data;
      }
      // Only a refresh skips the read cache — this is the reload that follows a save, where a cached
      // answer would still be the application from before the write.
      const options = { fresh: refresh === true };
      if (applicationId) {
        return await getApplicationWithId(applicationId, true, options);
      } else if (data !== undefined) {
        return await getApplicationWithId(data.id, true, options);
      }
    },
    [data, applicationId, getApplicationWithId]
  );
  const { value: loaded, status: localStatus, execute: updateApplication } = useAsync(getApplication);
  const status = data !== undefined && externalStatus ? externalStatus : localStatus;
  /* Read off the local hook, not the merged `status` above: whenever the page supplies data it also
     supplies its own status, which knows nothing about the refresh this view just ran. The loss grid
     needs the answer because a reload that failed leaves it holding pre-save rows. */
  const refreshFailed = localStatus === 'error';
  /* useAsync nulls its value when a call rejects, so a refresh that fails would otherwise drop this view
     to its empty state — unmounting the Loss History tab and with it the staged rows a failed save left
     in the grid, which exist nowhere else. The last application that did load stays on screen instead,
     and the empty/error states below are reserved for never having loaded one. */
  const requestedId = applicationId || (data && data.id);
  const lastLoaded = useRef(undefined);
  if (loaded) {
    lastLoaded.current = loaded;
  }
  /* Both candidates are matched on their own id against the id being asked for, and neither is trusted
     for being the newer of the two: useAsync holds its previous value all the way through the next call,
     so opening a second application would render the first one's details and losses under the second's
     id until that fetch answered. Whichever way this view ends up with the wrong application, it shows
     no application at all. */
  const isRequested = (candidate) => !!candidate && requestedId != null && String(candidate.id) === String(requestedId);
  const application = isRequested(loaded) ? loaded : isRequested(lastLoaded.current) ? lastLoaded.current : undefined;
  const [bindApplicationDialog, setBindApplicationDialog] = useState({ open: false, data: undefined });
  const [packages, setPackages] = useState();
  const showBindApp = application?.product?.name === 'Auto Hail-Safe-Max10' || application?.product?.name === 'Auto Hail-Safe';

  const fetchPackages = async () => {
    try {
      const packages = await getPackages();
      setPackages(packages);
    } catch (err) {
      console.log(err.message || 'An error occurred while fetching packages.');
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const onDialogClose = () => setBindApplicationDialog({ open: false });

  const handlePressBindApplication = () => {
    setBindApplicationDialog({ open: true, data: [] });
  };

  const containerProps = {
    bg: 'white',
    ref,
    ...(status === 'pending' || !application ? { justifyContent: 'center', alignItems: 'center' } : null),
    padTop: '$3',
    ...rest,
  };
  const contents = (
    /* Main caps a column layout at $mainWidth (920px), which the Loss History grid's seventeen
       columns do not fit in. PageContent keeps its own horizontal padding, so the card still
       breathes at full width. */
    <Main maxWidth="100%" pointerEvents="box-none" padBottom={164}>
      {application && application.id ? (
        <LayoutBox
          padX="$3"
          pb="$1.5"
          flexDirection={({ theme }) => theme.breakpoints({ xs: 'column-reverse', sm: 'row' })}
          layout="bottom-left"
          justifyContent="space-between"
          width="100%"
          gap={8}
        >
          <LayoutBox gap={8} flexDirection="row">
            <GenerateQuoteForApplicationButton
              applicationId={application.id}
              onSuccess={({ id: quoteId } = {}) => {
                if (quoteId) {
                  navigate.to(`/quotes/${quoteId}`);
                }
              }}
            />
            {showBindApp && <Button label="Bind Application" onPress={handlePressBindApplication} />}
            <BindApplicationDialog
              applicationId={application.id}
              packages={packages}
              onClose={onDialogClose}
              onSuccess={({ id: policyId } = {}) => {
                if (policyId) {
                  navigate.to(`/policies/${policyId}`);
                }
              }}
              {...bindApplicationDialog}
            />
          </LayoutBox>
        </LayoutBox>
      ) : null}
      <PageContent {...containerProps}>
        {/* The spinner is for having nothing to show, not for being busy: an in-flight refresh over an
            application already on screen has to leave the tabs mounted, or it unmounts the loss grid and
            takes the staged rows a failed save left in it. Being busy is what the tabs' own `refreshing`
            prop is for, and it only reaches them if they are still rendered. */}
        {status === 'pending' && !application ? (
          <ActivityIndicator />
        ) : !application && status === 'error' ? (
          <Text>Error: Could not retrieve application data</Text>
        ) : !application ? (
          <Text>No application data available</Text>
        ) : (
          <>
            <ApplicationTabs data={application} initialTab={initialTab} onTabChanged={setViewTab} />
            <ApplicationTabView
              tab={viewTab}
              data={application}
              status={status}
              scrollNode
              update={updateApplication}
              refreshFailed={refreshFailed}
            />
          </>
        )}
      </PageContent>
    </Main>
  );
  if (!scrollView) {
    return contents;
  }
  return (
    <ScrollView width="100%" flex={1} minHeight="101%" padTop="100" containerStyle={{ minHeight: '101%' }} provideNode {...rest}>
      {contents}
      {children}
    </ScrollView>
  );
});

const applicationTabsKeys = Object.keys(ApplicationViewTabs);
const ApplicationTabs = ({ data, initialTab = ApplicationViewTabs.APPLICATION, ...props }) => {
  const numQuotes = useMemo(() => {
    if (data) {
      if (data.quotes && Array.isArray(data.quotes) && data.quotes.length) {
        return data.quotes.length;
      }
    }
    return 0;
  }, [data]);

  const getLabel = useCallback(
    (name, label) => {
      if (name === ApplicationViewTabs.APPLICATION) {
        return 'Application Details';
      }
      if (name === ApplicationViewTabs.QUOTES) {
        return `Quotes (${numQuotes})`;
      }
      if (name === ApplicationViewTabs.DOCUMENTS) {
        return `Documents`;
      }
      if (name === ApplicationViewTabs.LOSS_HISTORY) {
        return `Loss History`;
      }
      return label;
    },
    [numQuotes]
  );

  return (
    <Box width="100%" alignItems="flex-start">
      {/* currentTab, not initialTab: ViewTabs only seeds/syncs from currentTab, and it's constant here,
          so this seeds the selection once without letting ViewTabs' mount-report snap it back. */}
      <ViewTabs currentTab={initialTab} getLabel={getLabel} tabs={applicationTabsKeys} {...props} />
    </Box>
  );
};

const ApplicationTabView = ({ tab, data, status, scrollNode, update, refreshFailed }) => {
  switch (tab) {
    case ApplicationViewTabs.APPLICATION:
      return <ApplicationDetails data={data} refreshApplicationData={update} refreshing={status === 'pending'} />;
    case ApplicationViewTabs.QUOTES:
      return <ApplicationQuotes quotes={data ? data.quotes : []} status={status} scrollNode={scrollNode} />;
    case ApplicationViewTabs.DOCUMENTS:
      return <ApplicationDocuments data={data} update={update} />;
    case ApplicationViewTabs.LOSS_HISTORY:
      return <LossManagement application={data} refreshApplicationData={update} refreshing={status === 'pending'} refreshFailed={refreshFailed} />;
    default:
      return <ApplicationDetails data={data} />;
  }
};

const ApplicationQuotes = React.forwardRef(({ quotes, status, scrollNode }, ref) => {
  return <QuoteList ref={ref} data={quotes && Array.isArray(quotes) && quotes.length ? quotes : []} loading={status === 'pending'} scrollNode />;
});
